import { useCopyToClipboard } from 'react-use';

import { type Board } from '@air/api/types';
import { CheckIcon } from '@air/design-icons';
import { Tooltip } from '@air/primitive-tooltip';
import { useAirModal } from '@air/provider-modal';

import { DemoFormModal } from '~/components/Modals/DemoFormModal';
import { useIsApiAdmin } from '~/hooks/useIsApiAdmin';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { canManageSubscription } from '~/utils/permissions/workspacePermissions';

export type BoardIdProps = Required<Pick<Board, 'id'>>;

export const BoardId = ({ id }: BoardIdProps) => {
  const { isApiAdmin } = useIsApiAdmin();
  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();
  const [showContactSalesModal] = useAirModal(DemoFormModal);
  const [state, copyToClipboard] = useCopyToClipboard();

  const canEditPlan = canManageSubscription(workspacePermissions);

  return (
    <div className="flex flex-col items-start gap-1">
      <div className="font-medium ads:text-metadata text-12 text-gray-10">Board ID</div>

      <div className="flex gap-1">
        {isApiAdmin ? (
          <>
            <Tooltip label="Copy to clipboard">
              <button
                className="select-all ads:text-metadata text-12 text-gray-9"
                // biome-ignore lint/performance/noJsxPropsBind: copy writes this board id to the clipboard
                onClick={() => copyToClipboard(id)}
                type="button"
              >
                {id}
              </button>
            </Tooltip>

            {!!state.value && <CheckIcon className="ads:text-icon-primary text-blue-9" size="sm" />}
          </>
        ) : (
          <div className="ads:text-metadata text-12 text-gray-9">
            Upgrade to Business or Enterprise to access API info.{' '}
            {canEditPlan ? (
              <button
                className="mr-1 inline ads:text-primary text-gray-12 hover:underline"
                // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
                onClick={() =>
                  showContactSalesModal({
                    uiLocation: 'asset_modal_api_cta',
                  })
                }
                type="button"
              >
                Contact sales →
              </button>
            ) : (
              'Contact a workspace admin to upgrade.'
            )}
          </div>
        )}
      </div>
    </div>
  );
};
